/**
 * Flooring Calculator — Web Component
 *
 * Metafields read via data-* attributes (set in flooring-calculator.liquid):
 *   data-pack-coverage            — m² coverage per pack for the current variant/product
 *   data-product-pack-coverage    — product-level fallback pack coverage
 *   data-variant-pack-coverages   — JSON map of { variantId: packCoverage|null } for all variants
 *   data-pack-price           — price of one pack, in the currency's minor units
 *   data-currency             — ISO code used to format the total
 *   data-wastage              — wastage % (theme default, overridable per-product)
 *   data-unit                 — "m" or "ft"; the default unit shown on load
 *   data-current-variant-id   — the initially selected variant ID
 *   data-section-id           — used to filter PubSub variantChange events
 */

if (!customElements.get('flooring-calculator')) {
  customElements.define(
    'flooring-calculator',
    class FlooringCalculator extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.unit = this.dataset.unit || 'm';
        this.wastage = parseFloat(this.dataset.wastage) || 0;
        this.packCoverage = parseFloat(this.dataset.packCoverage) || null;
        this.productPackCoverage = parseFloat(this.dataset.productPackCoverage) || null;
        this.currentVariantId = parseInt(this.dataset.currentVariantId) || null;
        this.packPrice = parseInt(this.dataset.packPrice);
        this.currency = this.dataset.currency || 'GBP';

        try {
          this.variantPackCoverages = JSON.parse(this.dataset.variantPackCoverages || '{}');
        } catch (_) {
          this.variantPackCoverages = {};
        }

        const inputs = this.querySelectorAll('.flooring-calculator__input');
        this.widthInput = inputs[0];
        this.lengthInput = inputs[1];
        this.wastageCheckbox = this.querySelector('.flooring-calculator__wastage-checkbox');
        this.resultEl = this.querySelector('.flooring-calculator__result');
        this.packsNumberEl = this.querySelector('.flooring-calculator__packs-number');
        this.totalEl = this.querySelector('.flooring-calculator__total');
        this.detailEl = this.querySelector('.flooring-calculator__result-detail');
        this.unitBtns = this.querySelectorAll('.flooring-calculator__unit-btn');
        this.unitLabels = this.querySelectorAll('.flooring-calculator__unit-label');

        this.widthInput?.addEventListener('input', () => this.calculate());
        this.lengthInput?.addEventListener('input', () => this.calculate());
        this.wastageCheckbox?.addEventListener('change', () => this.calculate());

        this.unitBtns.forEach((btn) => {
          btn.addEventListener('click', () => this.setUnit(btn.dataset.unit));
        });

        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this._unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.sectionId) return;
            const variant = event.data.variant;
            if (!variant) return;

            this.currentVariantId = variant.id;
            const variantId = String(variant.id);
            const variantCoverage = this.variantPackCoverages[variantId];
            this.packCoverage = (variantCoverage != null) ? parseFloat(variantCoverage) || null : this.productPackCoverage;
            if (typeof variant.price === 'number') this.packPrice = variant.price;

            this.hidden = !this.packCoverage;
            this.calculate();
          });
        }
      }

      disconnectedCallback() {
        this._unsubscribe?.();
      }

      setUnit(unit) {
        if (unit === this.unit) return;

        const prevUnit = this.unit;
        this.unit = unit;

        // Convert existing input values to the new unit
        [this.widthInput, this.lengthInput].forEach((input) => {
          const val = parseFloat(input?.value);
          if (!input || !val) return;
          if (prevUnit === 'm' && unit === 'ft') {
            input.value = (val * 3.28084).toFixed(2);
          } else if (prevUnit === 'ft' && unit === 'm') {
            input.value = (val * 0.3048).toFixed(2);
          }
        });

        this.unitBtns.forEach((btn) => {
          btn.classList.toggle('flooring-calculator__unit-btn--active', btn.dataset.unit === unit);
        });
        this.unitLabels.forEach((el) => (el.textContent = unit));
        this.calculate();
      }

      _getQuantityInput() {
        // Walk up to the product section, then find the quantity input within it
        const section =
          this.closest('[data-section]') ||
          document.getElementById(`shopify-section-${this.sectionId}`);
        return (
          section?.querySelector('input.quantity__input') ||
          section?.querySelector('input[name="quantity"]')
        );
      }

      _setPageQuantity(packs) {
        const qtyInput = this._getQuantityInput();
        if (!qtyInput) return;

        const min = parseInt(qtyInput.min) || 1;
        const max = qtyInput.max ? parseInt(qtyInput.max) : Infinity;
        const clamped = Math.min(Math.max(packs, min), max);

        qtyInput.value = clamped;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /**
       * Shopify prices are integers in the currency's minor units (pence for GBP).
       * Intl handles the symbol and separators for whichever currency the cart is in.
       */
      _formatMoney(minorUnits) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
            style: 'currency',
            currency: this.currency,
          }).format(minorUnits / 100);
        } catch (_) {
          return (minorUnits / 100).toFixed(2);
        }
      }

      calculate() {
        const width = parseFloat(this.widthInput?.value) || 0;
        const length = parseFloat(this.lengthInput?.value) || 0;

        if (width <= 0 || length <= 0 || !this.packCoverage) {
          if (this.resultEl) this.resultEl.hidden = true;
          return;
        }

        let areaSqM = width * length;
        if (this.unit === 'ft') areaSqM *= 0.092903;

        const applyWastage = this.wastageCheckbox?.checked ?? true;
        const multiplier = applyWastage ? 1 + this.wastage / 100 : 1;
        const adjustedArea = areaSqM * multiplier;
        const packs = Math.ceil(adjustedArea / this.packCoverage);

        if (this.packsNumberEl) this.packsNumberEl.textContent = packs;

        if (this.totalEl) {
          const hasPrice = Number.isFinite(this.packPrice) && this.packPrice > 0;
          this.totalEl.hidden = !hasPrice;
          if (hasPrice) {
            this.totalEl.textContent = `${this._formatMoney(packs * this.packPrice)} total (${packs} × ${this._formatMoney(this.packPrice)} per pack)`;
          }
        }

        let detail = `Room: ${areaSqM.toFixed(2)} m²`;
        if (applyWastage && this.wastage > 0) {
          detail += ` + ${this.wastage}% wastage = ${adjustedArea.toFixed(2)} m²`;
        }
        detail += ` · ${this.packCoverage} m² per pack`;
        if (this.detailEl) this.detailEl.textContent = detail;

        this._currentPacks = packs;
        this._setPageQuantity(packs);

        if (this.resultEl) this.resultEl.hidden = false;
      }
    }
  );
}
